// COMMON TRADE PANEL row model (redesign stage 1). Options / Iron Condor / Box rows arrive normalised from
// GET /api/paper-book/common-trades (backend common_trades.py); Futures spreads are normalised HERE from the existing
// GET /api/paper/v25/trades view - the same figures the Futures card shows. Display only: nothing is recalculated
// except the label of what each figure is.

export const CATEGORIES = [["ALL", "All Trades"], ["OPTIONS", "Options"], ["FUTURES", "Futures"], ["IRON_CONDOR", "Iron Condor"], ["BOX", "Box Spread"]];

export const STRATEGY = {
  FUTURES: { label: "FUTURE", cls: "bg-[#2563EB]/15 text-[#2563EB]" },
  BUY_OPTIONS: { label: "OPTION BUY", cls: "bg-[#00C805]/15 text-[#008a04]" },
  SELL_OPTIONS: { label: "OPTION SELL", cls: "bg-[#B45309]/15 text-[#B45309]" },
  IRON_CONDOR: { label: "CONDOR", cls: "bg-[#ea580c]/15 text-[#ea580c]" },
  BOX: { label: "BOX", cls: "bg-[#7c3aed]/15 text-[#7c3aed]" },
};

// what each P&L IS - shown under every figure; bases are never added together
export const BASIS = {
  NET_TO_CLOSE_BID: "net to close at bid · after charges",
  NET_TO_CLOSE_ASK: "net to close at ask · after charges",
  NET_LIQUIDATION: "net liquidation · after charges",
  GROSS_TO_CLOSE: "to close · gross, before charges",
  REALISED_NET: "realised · net of charges",
  REALISED_GROSS: "realised · gross, before charges",
  GROSS_MARK: "gross mark at LTP · before charges",
  REALISED_MARK: "cycle complete · gross",
};

import { futuresNet } from "@/lib/netPnl";

const n = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

/** One Futures spread view (GET /paper/v25/trades) as a panel row. */
export function futuresRow(t) {
  const row = t.current_row || {};
  const seg = t.market_segment === "NFO" ? "NFO" : "MCX";
  const done = t.status === "COMPLETE";
  const openPos = (t.open_levels || 0) > 0 || (t.exposed_levels || 0) > 0 || (t.partial_pairs || 0) > 0 || t.status === "EXITING";
  const entry = n(t.actual_average_entry_spread);
  const now = n(t.current_spread);
  const next = n(t.next_level_threshold);
  const net = futuresNet(t);
  return {
    key: `FUTURES:${t.id}`, id: t.id, category: "FUTURES", strategy: "FUTURES", name: t.name,
    environment: t.environment || t.trading_environment || "PAPER", status: t.status, finished: done,
    market: seg, underlying: t.assigned_commodity || "—", expiry: t.near_expiry || null,
    exit_by: null, dte: t.earlier_leg_dte ?? null,
    spot: n(seg === "NFO" ? row.spot_ltp : row.near_ltp), spot_label: seg === "NFO" ? "spot" : "near LTP",
    pnl: n(t.cumulative_gross_pnl ?? t.combined_pnl), pnl_basis: done ? "REALISED_MARK" : "GROSS_MARK",
    indicative_ltp: null,
    net_pnl_ltp: net.ltp, net_pnl_bidask: net.bidask, bidask_basis: "long legs at the bid, short legs at the ask",
    // the NET target (compared with NET P&L at LTP); a Profit % trade's base is capital used + charges
    target: n(t.target_pnl), target_text: t.target_percent != null && t.target_source !== "G24"
      ? `${t.target_percent}% of ${t.target_basis || "capital used"}` : null, stop: null,
    scale: entry != null || now != null ? { kind: "SPREAD", entry, now, next } : null,
    position: { text: `${t.entered_levels ?? 0} lvl${t.open_hedged_pairs ? ` · ${t.open_hedged_pairs} pr` : ""}`, unit: "levels" },
    protection: null,
    risk_text: t.earlier_leg_dte != null ? `${t.earlier_leg_dte}d to near expiry${(t.rollover || {}).state && t.rollover.state !== "NONE" ? ` · ${t.rollover.state}` : ""}` : null,
    auto: { on: !!t.auto_square_off, editable: !done, kind: "FUTURES_AUTO_SQ" },
    price_state: null, as_of: null, stale: (t.warnings || []).some?.((w) => w?.status === "MARKET_DATA_STALE") || false,
    needs_attention: ["AWAITING_ACKNOWLEDGEMENT", "RECOVERY_PENDING"].includes(t.status),
    can_square_off: !done && openPos,
    contracts: [t.sell_symbol, t.buy_symbol].filter(Boolean),
    created_at: t.created_at, futures: t,
  };
}

export function matchesSearch(r, q) {
  if (!q) return true;
  const s = q.trim().toUpperCase();
  return [r.underlying, r.name, ...(r.contracts || [])].some((x) => String(x || "").toUpperCase().includes(s));
}

export function counts(rows) {
  const c = { ALL: rows.length };
  for (const [k] of CATEGORIES.slice(1)) c[k] = rows.filter((r) => r.category === k).length;
  return c;
}

/** Totals PER BASIS - never one blended P&L across bases. [[basis, total, count]] */
export function totalsByBasis(rows) {
  const m = new Map();
  for (const r of rows) {
    if (r.pnl == null) continue;
    const t = m.get(r.pnl_basis) || [0, 0];
    m.set(r.pnl_basis, [t[0] + r.pnl, t[1] + 1]);
  }
  return [...m.entries()].map(([b, [v, k]]) => [b, Math.round(v * 100) / 100, k]);
}

export function dedupe(rows) {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.key) ? false : (seen.add(r.key), true)));
}
